import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Printer } from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminDataToolbar } from "@/components/admin/import/AdminDataToolbar";
import { RefreshBar } from "@/components/admin/RefreshBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { toneClass, type OpsTone } from "@/lib/admin/order-badges";
import { getOpsDashboard } from "@/lib/admin/operations.functions";
import {
  getKitchenProduction,
  setPreparedQty,
} from "@/lib/admin/production.functions";
import { printProductionSheet } from "@/lib/admin/production-print";
import { getIngredientRequirements } from "@/lib/admin/recipes.functions";
import { formatPaise } from "@/lib/format/money";
import {
  MEAL_SLOTS,
  MEAL_SLOT_LABEL,
  PRODUCTION_CATEGORIES,
  PRODUCTION_CATEGORY_LABEL,
  addDays,
  istToday,
  type KitchenProduction,
} from "@/lib/admin/production.schemas";
import { ORDER_SOURCES, ORDER_SOURCE_LABEL } from "@/lib/admin/ops-sources";

export const Route = createFileRoute("/_admin/admin/kitchen-production")({
  head: () => ({
    meta: [
      { title: "Kitchen Production — Admin" },
      {
        name: "description",
        content:
          "Aggregated production quantities per meal slot, category, dish and order source for the selected service day.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: KitchenProductionPage,
});

function inr(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function SummaryCard({
  label,
  value,
  tone,
  loading,
}: {
  label: string;
  value: string | number;
  tone: OpsTone;
  loading: boolean;
}) {
  return (
    <div className={`rounded-lg border p-3 ${toneClass(tone)}`}>
      <p className="truncate text-[11px] font-medium uppercase tracking-wide opacity-80">
        {label}
      </p>
      {loading ? (
        <Skeleton className="mt-2 h-6 w-12" />
      ) : (
        <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      )}
    </div>
  );
}

function KitchenProductionPage() {
  const queryClient = useQueryClient();
  const [day, setDay] = useState(istToday());
  const [slot, setSlot] = useState("");
  const [category, setCategory] = useState("");
  const [area, setArea] = useState("");
  const [veg, setVeg] = useState("");
  const [source, setSource] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const dashboardFn = useServerFn(getOpsDashboard);
  const productionFn = useServerFn(getKitchenProduction);
  const preparedFn = useServerFn(setPreparedQty);

  const dashboard = useQuery({
    queryKey: ["admin", "ops-dashboard", day],
    queryFn: () => dashboardFn({ data: { day } }),
    staleTime: 5_000,
    placeholderData: (prev) => prev,
  });

  const production = useQuery<KitchenProduction>({
    queryKey: ["admin", "kitchen-production", day, slot, category, area, veg, source],
    queryFn: () =>
      productionFn({
        data: {
          date: day,
          slot: slot ? (slot as never) : undefined,
          category: category ? (category as never) : undefined,
          area: area || undefined,
          veg: veg ? (veg as never) : undefined,
          source: source ? (source as never) : undefined,
        },
      }),
    staleTime: 5_000,
    placeholderData: (prev) => prev,
  });

  const save = useMutation({
    mutationFn: (v: { slot: string; kind: string; key: string; qty: number }) =>
      preparedFn({ data: { date: day, ...v } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "kitchen-production"] });
    },
    onError: () => toast.error("Couldn't save the prepared quantity."),
  });

  const data = production.data;
  const loading = dashboard.isLoading;
  const channel = (k: string) => dashboard.data?.by_channel?.[k] ?? 0;
  const status = (k: string) => dashboard.data?.by_status?.[k] ?? 0;

  const areas = useMemo(() => data?.areas ?? [], [data]);
  const slotBucket = (s: string) => data?.slots.find((x) => x.slot === s);
  const catBucket = (c: string) => data?.categories.find((x) => x.category === c);

  return (
    <div>
      <AdminPageHeader
        title="Kitchen Production"
        description="What needs to be prepared for the selected service day, aggregated across every order source."
        actions={
          <AdminDataToolbar
            exportName={`kitchen-production-${day}`}
            moduleId="kitchen-production"
            onImported={() =>
              void queryClient.invalidateQueries({ queryKey: ["admin", "kitchen-production"] })
            }
            rows={() =>
              (data?.dishes ?? []).map((d) => ({
                "Production Date": day,
                "Meal Slot": d.slot,
                "Item Kind": d.item_kind,
                "Item Key": d.item_key,
                Title: d.title,
                Ordered: d.ordered,
                "Prepared Qty": d.prepared,
                Remaining: d.remaining,
              }))
            }
          />
        }
      />


      <RefreshBar
        targets={[
          { key: "ops-dashboard", label: "summary" },
          { key: "kitchen-production", label: "production" },
        ]}
        busy={production.isFetching || dashboard.isFetching}
        lastUpdated={production.dataUpdatedAt || null}
      />

      {/* ------------------------------------------------ live summary */}
      <section aria-label="Live summary" className="mb-6 space-y-3">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-6">
          <SummaryCard label="Website" value={channel("website")} tone="info" loading={loading} />
          <SummaryCard label="Phone" value={channel("phone")} tone="teal" loading={loading} />
          <SummaryCard label="Walk-in" value={channel("walkin")} tone="warn" loading={loading} />
          <SummaryCard
            label="WhatsApp"
            value={channel("whatsapp")}
            tone="success"
            loading={loading}
          />
          <SummaryCard
            label="Corporate"
            value={channel("corporate")}
            tone="purple"
            loading={loading}
          />
          <SummaryCard
            label="Subscription"
            value={channel("subscription")}
            tone="neutral"
            loading={loading}
          />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
          <SummaryCard
            label="Cancelled"
            value={status("cancelled") + status("failed")}
            tone="danger"
            loading={loading}
          />
          <SummaryCard
            label="Revenue"
            value={inr(dashboard.data?.revenue_paise ?? 0)}
            tone="success"
            loading={loading}
          />
          <SummaryCard
            label="Pending"
            value={status("new") + status("confirmed") + status("accepted")}
            tone="neutral"
            loading={loading}
          />
          <SummaryCard label="Preparing" value={status("preparing")} tone="warn" loading={loading} />
          <SummaryCard label="Ready" value={status("ready")} tone="teal" loading={loading} />
          <SummaryCard
            label="Out for delivery"
            value={status("out_for_delivery")}
            tone="purple"
            loading={loading}
          />
          <SummaryCard
            label="Delivered"
            value={status("delivered")}
            tone="success"
            loading={loading}
          />
        </div>
      </section>

      {/* ---------------------------------------------------- filters */}
      <section aria-label="Filters" className="mb-6 flex flex-wrap items-end gap-2">
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => setDay(istToday())}>
            Today
          </Button>
          <Button size="sm" variant="outline" onClick={() => setDay(addDays(istToday(), 1))}>
            Tomorrow
          </Button>
        </div>
        <Input
          type="date"
          value={day}
          onChange={(e) => setDay(e.target.value || istToday())}
          className="h-9 w-40"
          aria-label="Production date"
        />
        <select
          value={slot}
          onChange={(e) => setSlot(e.target.value)}
          aria-label="Meal slot"
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All slots</option>
          {MEAL_SLOTS.map((s) => (
            <option key={s} value={s}>
              {MEAL_SLOT_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          aria-label="Category"
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All categories</option>
          {PRODUCTION_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {PRODUCTION_CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>
        <select
          value={area}
          onChange={(e) => setArea(e.target.value)}
          aria-label="Delivery area"
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All areas</option>
          {areas.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select
          value={veg}
          onChange={(e) => setVeg(e.target.value)}
          aria-label="Diet"
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">Veg &amp; Non-veg</option>
          <option value="veg">Veg</option>
          <option value="non_veg">Non-veg</option>
        </select>
        <select
          value={source}
          onChange={(e) => setSource(e.target.value)}
          aria-label="Order source"
          className="h-9 rounded-md border border-input bg-background px-2 text-sm"
        >
          <option value="">All sources</option>
          {ORDER_SOURCES.map((s) => (
            <option key={s} value={s}>
              {ORDER_SOURCE_LABEL[s]}
            </option>
          ))}
        </select>
      </section>

      {/* ------------------------------------------- production summary */}
      <section aria-label="Production summary" className="mb-6">
        <h2 className="mb-2 text-sm font-medium text-foreground">Production summary</h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <SummaryCard
            label="Portions ordered"
            value={data?.totals.ordered ?? 0}
            tone="info"
            loading={production.isLoading}
          />
          <SummaryCard
            label="Prepared"
            value={data?.totals.prepared ?? 0}
            tone="success"
            loading={production.isLoading}
          />
          <SummaryCard
            label="Remaining"
            value={data?.totals.remaining ?? 0}
            tone="warn"
            loading={production.isLoading}
          />
          <SummaryCard
            label="Distinct items"
            value={data?.totals.items ?? 0}
            tone="neutral"
            loading={production.isLoading}
          />
        </div>
      </section>

      {/* --------------------------------------- ingredient requirements */}
      <IngredientRequirementsPanel day={day} slot={slot} />



      <div className="mb-6 grid gap-6 lg:grid-cols-2">
        <section aria-label="Meal slot summary">
          <h2 className="mb-2 text-sm font-medium text-foreground">Meal slot summary</h2>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Slot</th>
                  <th className="px-3 py-2 text-right">Ordered</th>
                  <th className="px-3 py-2 text-right">Prepared</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {MEAL_SLOTS.map((s) => {
                  const b = slotBucket(s);
                  return (
                    <tr key={s} className="border-t border-border">
                      <td className="px-3 py-2">{MEAL_SLOT_LABEL[s]}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b?.ordered ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b?.prepared ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b?.remaining ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section aria-label="Category summary">
          <h2 className="mb-2 text-sm font-medium text-foreground">Category summary</h2>
          <div className="overflow-hidden rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-right">Ordered</th>
                  <th className="px-3 py-2 text-right">Prepared</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                </tr>
              </thead>
              <tbody>
                {PRODUCTION_CATEGORIES.map((c) => {
                  const b = catBucket(c);
                  return (
                    <tr key={c} className="border-t border-border">
                      <td className="px-3 py-2">{PRODUCTION_CATEGORY_LABEL[c]}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b?.ordered ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b?.prepared ?? 0}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b?.remaining ?? 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </div>

      {/* ------------------------------------------------- printing */}
      <section aria-label="Printing" className="mb-4 flex flex-wrap gap-2">
        {(["breakfast", "lunch", "snacks", "dinner"] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant="outline"
            disabled={!data}
            onClick={() => data && printProductionSheet(data, s)}
            className="gap-1.5"
          >
            <Printer className="h-3.5 w-3.5" aria-hidden />
            {MEAL_SLOT_LABEL[s]} sheet
          </Button>
        ))}
        <Button
          size="sm"
          disabled={!data}
          onClick={() => data && printProductionSheet(data, null)}
          className="gap-1.5"
        >
          <Printer className="h-3.5 w-3.5" aria-hidden />
          Complete daily production sheet
        </Button>
      </section>

      {/* --------------------------------------------- dish production */}
      <section aria-label="Dish production">
        <h2 className="mb-2 text-sm font-medium text-foreground">Dish production</h2>
        {production.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : production.error ? (
          <p className="text-sm text-destructive">Couldn't load production data.</p>
        ) : (data?.dishes.length ?? 0) === 0 ? (
          <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
            Nothing to prepare for this selection.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Dish</th>
                  <th className="px-3 py-2 text-left">Category</th>
                  <th className="px-3 py-2 text-left">Slot</th>
                  <th className="px-3 py-2 text-right">Ordered</th>
                  <th className="px-3 py-2 text-right">Prepared</th>
                  <th className="px-3 py-2 text-right">Pending</th>
                  <th className="px-3 py-2 text-right">Remaining</th>
                  <th className="px-3 py-2 text-left">Sources</th>
                </tr>
              </thead>
              <tbody>
                {(data?.dishes ?? []).map((d) => {
                  const rowKey = `${d.slot}:${d.item_key}`;
                  const open = expanded[rowKey] ?? false;
                  return (
                    <tr key={rowKey} className="border-t border-border align-top">
                      <td className="px-3 py-2 font-medium">{d.title}</td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {PRODUCTION_CATEGORY_LABEL[d.category] ?? d.category}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {MEAL_SLOT_LABEL[d.slot] ?? d.slot}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{d.ordered}</td>
                      <td className="px-3 py-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          defaultValue={d.prepared}
                          key={`${rowKey}-${d.prepared}`}
                          className="ml-auto h-8 w-20 text-right"
                          aria-label={`Prepared quantity for ${d.title}`}
                          onBlur={(e) => {
                            const qty = Number(e.target.value);
                            if (!Number.isFinite(qty) || qty === d.prepared) return;
                            save.mutate({
                              slot: d.slot,
                              kind: d.item_kind,
                              key: d.item_key,
                              qty: Math.max(0, Math.round(qty)),
                            });
                          }}
                        />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{d.pending}</td>
                      <td className="px-3 py-2 text-right font-semibold tabular-nums">
                        {d.remaining}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-xs text-muted-foreground underline-offset-2 hover:underline"
                          onClick={() =>
                            setExpanded((prev) => ({ ...prev, [rowKey]: !open }))
                          }
                        >
                          {open ? "Hide breakdown" : "Order source breakdown"}
                        </button>
                        {open ? (
                          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                            <li>Website · {d.sources.website}</li>
                            <li>Phone · {d.sources.phone}</li>
                            <li>Walk-in · {d.sources.walkin}</li>
                            <li>WhatsApp · {d.sources.whatsapp}</li>
                            <li>Corporate · {d.sources.corporate}</li>
                            <li>Subscription · {d.sources.subscription}</li>
                            <li className="font-medium text-foreground">Total · {d.ordered}</li>
                          </ul>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Ingredient requirements for the selected service day — warns about low or
 * out-of-stock items without ever blocking production.
 */
function IngredientRequirementsPanel({ day, slot }: { day: string; slot: string }) {
  const fn = useServerFn(getIngredientRequirements);
  const q = useQuery({
    queryKey: ["admin", "ingredient-requirements", day, slot],
    queryFn: () => fn({ data: { date: day, ...(slot ? { slot } : {}) } }),
    staleTime: 10_000,
  });
  const rows = q.data ?? [];
  const short = rows.filter((r) => r.shortage_qty > 0 || r.stock_state !== "ok");

  return (
    <section aria-label="Ingredient requirements" className="mb-6">
      <h2 className="mb-2 text-sm font-medium text-foreground">Ingredient requirements</h2>
      {q.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : rows.length === 0 ? (
        <p className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
          No recipe ingredients are linked to this day's orders yet.
        </p>
      ) : (
        <>
          {short.length > 0 ? (
            <p className="mb-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
              {short.length} ingredient(s) are low or short for this day. Orders are not blocked —
              restock or adjust production.
            </p>
          ) : null}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Ingredient</th>
                  <th className="px-3 py-2 text-right">Required</th>
                  <th className="px-3 py-2 text-right">In stock</th>
                  <th className="px-3 py-2 text-right">Already used</th>
                  <th className="px-3 py-2 text-right">Shortage</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.inventory_item_id} className="border-t border-border">
                    <td className="px-3 py-2">
                      <span className="font-medium">{r.inventory_item_name}</span>
                      <span className="ml-2 text-[11px] text-muted-foreground">{r.sku}</span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.required_qty} {r.unit}
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${
                        r.stock_state === "out"
                          ? "text-destructive"
                          : r.stock_state === "low"
                            ? "text-amber-600 dark:text-amber-400"
                            : ""
                      }`}
                    >
                      {r.current_stock} {r.unit}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {r.consumed_qty} {r.unit}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {r.shortage_qty > 0 ? `${r.shortage_qty} ${r.unit}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatPaise(r.cost_paise)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
